import {
  AdminOrder,
  OrderBase,
  OrderItem,
  PurchaseOrder,
  PurchaseOrderDetails,
  SalesOrder,
  SalesOrderDetails,
  SimplifiedOrder,
} from '@lib/types';

type OrderItemDTO = OrderItem;

export interface SimplifiedOrderDTO extends SimplifiedOrder {
  items: OrderItemDTO[];
}

export interface PurchaseOrderDTO extends SimplifiedOrderDTO {
  seller: PurchaseOrder['seller'];
}

export interface SalesOrderDTO extends SimplifiedOrderDTO {
  buyer: SalesOrder['buyer'];
}

export interface AdminOrderDTO extends SimplifiedOrderDTO {
  seller: AdminOrder['seller'];
}

export interface PurchaseOrderDetailsDTO extends OrderBase {
  seller: PurchaseOrder['seller'];
  paymentExpiresAt?: string;
  pixCopyPaste?: string;
  pixQrCodeUrl?: string;
  senderAddress?: PurchaseOrderDetails['senderAddress'];
  shippingAddress: PurchaseOrderDetails['shippingAddress'];
}

export interface SalesOrderDetailsDTO extends OrderBase {
  buyer: SalesOrder['buyer'];
  senderAddress?: SalesOrderDetails['senderAddress'];
  shippingAddress: SalesOrderDetails['shippingAddress'];
}

const createOrderItem = (dto: OrderItemDTO): OrderItem => ({
  id: dto.id,
  cardId: dto.cardId,
  cardGameId: dto.cardGameId,
  cardSlug: dto.cardSlug,
  cardGameCode: dto.cardGameCode,
  cardName: dto.cardName,
  printingCode: dto.printingCode,
  printingId: dto.printingId,
  languageId: dto.languageId,
  quantity: dto.quantity,
  originalUnitPriceCents: dto.originalUnitPriceCents,
  promotionId: dto.promotionId,
  promotionName: dto.promotionName,
  promotionDiscountCents: dto.promotionDiscountCents,
  promotionEndsAt: dto.promotionEndsAt,
  unitPrice: dto.unitPrice,
  collectionName: dto.collectionName,
  rarityName: dto.rarityName,
  languageCode: dto.languageCode,
  condition: dto.condition,
  edition: dto.edition,
  imageUrl: dto.imageUrl,
});

const createSimplifiedOrder = (dto: SimplifiedOrderDTO): SimplifiedOrder => ({
  id: dto.id,
  status: dto.status,
  total: dto.total,
  subtotalCents: dto.subtotalCents,
  storeDiscountCents: dto.storeDiscountCents,
  platformDiscountCents: dto.platformDiscountCents,
  couponCodes: dto.couponCodes,
  shippingCents: dto.shippingCents,
  totalCents: dto.totalCents,
  paymentStatus: dto.paymentStatus,
  refundStatus: dto.refundStatus,
  dispute: dto.dispute ? { ...dto.dispute } : undefined,
  settlementStatus: dto.settlementStatus,
  shippingMethod: dto.shippingMethod,
  shippingServiceName: dto.shippingServiceName,
  shippingCarrierName: dto.shippingCarrierName,
  productItems: dto.productItems?.map((item) => ({ ...item })),
  items: dto.items.map(createOrderItem),
  createdAt: dto.createdAt,
  updatedAt: dto.updatedAt,
});

const createOrderDetails = (dto: OrderBase): OrderBase => ({
  ...createSimplifiedOrder(dto),
  deliveredAt: dto.deliveredAt,
  completedAt: dto.completedAt,
  deliveryIndicatedAt: dto.deliveryIndicatedAt,
  completionDeadlineAt: dto.completionDeadlineAt,
  deliveryProofFileName: dto.deliveryProofFileName,
  shippingDeadlineAt: dto.shippingDeadlineAt,
  shippedAt: dto.shippedAt,
  cancelledForNonShipmentAt: dto.cancelledForNonShipmentAt,
  nonShipmentJustification: dto.nonShipmentJustification,
  returnRequired: dto.returnRequired,
  returnReceivedAt: dto.returnReceivedAt,
  restockedAt: dto.restockedAt,
  carrier: dto.carrier,
  trackingCode: dto.trackingCode,
  shippingNotes: dto.shippingNotes,
  events: dto.events.map((event) => ({ ...event })),
  dispute: dto.dispute ? { ...dto.dispute } : undefined,
  review: dto.review ? { ...dto.review } : undefined,
  canOpenDispute: dto.canOpenDispute,
  canReview: dto.canReview,
  canComment: dto.canComment,
});

export const createPurchaseOrder = (dto: PurchaseOrderDTO): PurchaseOrder => ({
  ...createSimplifiedOrder(dto),
  seller: { ...dto.seller },
});

export const createSalesOrder = (dto: SalesOrderDTO): SalesOrder => ({
  ...createSimplifiedOrder(dto),
  buyer: { ...dto.buyer },
});

export const createAdminOrder = (dto: AdminOrderDTO): AdminOrder => ({
  ...createSimplifiedOrder(dto),
  seller: { ...dto.seller },
});

export const createPurchaseOrderDetails = (
  dto: PurchaseOrderDetailsDTO,
): PurchaseOrderDetails => ({
  ...createOrderDetails(dto),
  seller: { ...dto.seller },
  paymentExpiresAt: dto.paymentExpiresAt,
  pixCopyPaste: dto.pixCopyPaste,
  pixQrCodeUrl: dto.pixQrCodeUrl,
  senderAddress: dto.senderAddress ? { ...dto.senderAddress } : undefined,
  shippingAddress: { ...dto.shippingAddress },
});

export const createSalesOrderDetails = (
  dto: SalesOrderDetailsDTO,
): SalesOrderDetails => ({
  ...createOrderDetails(dto),
  financialSummary: dto.financialSummary
    ? {
        commissionCents: dto.financialSummary.commissionCents,
        commissionRateBps: dto.financialSummary.commissionRateBps,
        sellerNetCents: dto.financialSummary.sellerNetCents,
        estimated: dto.financialSummary.estimated,
      }
    : undefined,
  buyer: { ...dto.buyer },
  senderAddress: dto.senderAddress ? { ...dto.senderAddress } : undefined,
  shippingAddress: { ...dto.shippingAddress },
});
